'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AlertDescription } from '@kit/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { PasswordSignUpSchema } from '../schemas/password-sign-up.schema';
import InputAuth from './input';
import { TermsAndConditionsFormField } from './terms-and-conditions-form-field';
import { AuthButton } from './auth-button';

export function PasswordSignUpForm({
  defaultValues,
  displayTermsCheckbox,
  onSubmit,
  loading,
  error,
}: {
  defaultValues?: {
    email: string;
  };

  displayTermsCheckbox?: boolean;

  onSubmit: (params: {
    email: string;
    password: string;
    repeatPassword: string;
  }) => unknown;
  loading: boolean;
  error?: Error | null | undefined | string;
}) {
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(PasswordSignUpSchema),
    defaultValues: {
      email: defaultValues?.email ?? '',
      password: '',
      repeatPassword: '',
    },
  });

  const DefaultError = <Trans i18nKey="auth:errors.default" />;
  const errorCode = error instanceof Error ? error.message : error;

  return (
    <Form {...form}>
      <form
        className={'w-full space-y-2.5'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  data-test={'email-input'}
                  required
                  type="email"
                  placeholder={t('common:emailAddress')}
                  {...field}
                  error={!!form.formState.errors.email || !!error}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'password'}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  required
                  data-test={'password-input'}
                  type="password"
                  placeholder={t('common:password')}
                  {...field}
                  error={!!form.formState.errors.password || !!error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'repeatPassword'}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  required
                  data-test={'repeat-password-input'}
                  type="password"
                  placeholder={t('common:confirmPassword')}
                  {...field}
                  error={!!form.formState.errors.repeatPassword || !!error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <If condition={error}>
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className={'w-4 text-[#E48787]'} />
            <AlertDescription
              className="font-inter text-[12px] font-normal leading-[18px] text-[#E48787]"
              data-test={'auth-error-message'}
            >
              <Trans
                i18nKey={`auth:errors.${errorCode}`}
                defaults={'<DefaultError />'}
                components={{ DefaultError }}
              />
            </AlertDescription>
          </div>
        </If>

        <If condition={displayTermsCheckbox}>
          <TermsAndConditionsFormField />
        </If>

        <AuthButton
          data-test={'auth-submit-button'}
          loading={loading}
          disabled={loading}
          loadingText="auth:signingUp"
        >
          <Trans i18nKey={'auth:signUp'} />
        </AuthButton>
      </form>
    </Form>
  );
}
